import type {
  MembershipRoleChoiceResponse,
  OrganizationInvitationResponse,
  OrganizationRoleResponse,
} from "@kontextwerk/control-web-client"
import { rejectControlContext } from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { captureIdentityAdministration } from "../../../../../../../../../api/kehrblatt/identity-administration"

/** `off`: not requested for this owner; `unavailable`: the server does not offer the read yet. */
export type DirectoryResourceState = "off" | "loading" | "ready" | "failed" | "unavailable"

export type UsersDirectoryOptions = Readonly<{
  organizationId: string
  /** Full roles with their grants (core.roles view). */
  roles: boolean
  /** Assignable role choices and pending invitations (core.memberships manage). */
  manage: boolean
}>

const rejectsContext = (error: unknown) =>
  [401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)

/**
 * Roles, role choices and pending invitations of one confirmed organization context. The
 * context is captured once at creation; a context change clears the data and drops late answers.
 */
export const createUsersDirectory = (options: UsersDirectoryOptions) => {
  const binding = captureIdentityAdministration(options.organizationId)
  const context = binding?.session.context.kind === "organization" ? binding.session.context : null
  let disposed = false
  let available = $state(binding !== null)
  let roles = $state.raw<readonly OrganizationRoleResponse[]>([])
  let rolesState = $state<DirectoryResourceState>(binding && options.roles ? "loading" : "off")
  let choices = $state.raw<readonly MembershipRoleChoiceResponse[]>([])
  let choicesState = $state<DirectoryResourceState>(binding && options.manage ? "loading" : "off")
  let invitations = $state.raw<readonly OrganizationInvitationResponse[]>([])
  let invitationsState = $state<DirectoryResourceState>(
    binding && options.manage ? "loading" : "off",
  )
  const tickets = { roles: 0, choices: 0, invitations: 0 }
  const current = () => !disposed && binding !== null && binding.current()

  const invalidate = () => {
    available = false
    tickets.roles += 1
    tickets.choices += 1
    tickets.invitations += 1
    roles = []
    choices = []
    invitations = []
  }
  binding?.signal.addEventListener("abort", invalidate, { once: true })

  const failed = (error: unknown) => {
    if (current() && rejectsContext(error)) rejectControlContext()
  }

  const loadRoles = async () => {
    if (!binding || !options.roles || !current()) return

    const ticket = ++tickets.roles
    rolesState = "loading"
    try {
      const items = await binding.access.roles()

      if (ticket !== tickets.roles || !current()) return

      roles = items
      rolesState = "ready"
    } catch (error) {
      if (ticket !== tickets.roles || !current()) return

      rolesState = "failed"
      failed(error)
    }
  }

  const loadChoices = async () => {
    if (!binding || !options.manage || !current()) return

    const ticket = ++tickets.choices
    choicesState = "loading"
    try {
      const items = await binding.invitations.choices()

      if (ticket !== tickets.choices || !current()) return

      choices = items
      choicesState = "ready"
    } catch (error) {
      if (ticket !== tickets.choices || !current()) return

      choicesState = "failed"
      failed(error)
    }
  }

  const loadInvitations = async () => {
    if (!binding || !options.manage || !current()) return

    const ticket = ++tickets.invitations
    invitationsState = "loading"
    try {
      const items = await binding.invitations.listAll()

      if (ticket !== tickets.invitations || !current()) return

      invitations = items ?? []
      invitationsState = items ? "ready" : "unavailable"
    } catch (error) {
      if (ticket !== tickets.invitations || !current()) return

      invitations = []
      invitationsState = "failed"
      failed(error)
    }
  }

  let initialLoad: Promise<void> | null = null
  const loadAll = async () => {
    await Promise.all([loadRoles(), loadChoices(), loadInvitations()])
  }

  return {
    get available() {
      return available
    },
    get roles() {
      return roles
    },
    get rolesState() {
      return rolesState
    },
    get choices() {
      return choices
    },
    get choicesState() {
      return choicesState
    },
    get invitations() {
      return invitations
    },
    get invitationsState() {
      return invitationsState
    },
    /** The confirmed actor relationship; display and owner-protection hints only. */
    actor: context
      ? Object.freeze({
          membershipId: context.membership_id,
          roleId: context.role_id,
          roleName: context.role_name,
          delegated: context.delegation !== null,
        })
      : null,
    access: binding?.access ?? null,
    invitationsApi: binding?.invitations ?? null,
    current,
    /** Starts the initial reads once; every caller awaits the same settlement. */
    load: (): Promise<void> => (initialLoad ??= loadAll()),
    reloadRoles: loadRoles,
    reloadInvitations: loadInvitations,
    dispose() {
      disposed = true
      binding?.signal.removeEventListener("abort", invalidate)
      invalidate()
    },
  }
}
export type UsersDirectory = ReturnType<typeof createUsersDirectory>
